import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Plus, Pencil, Trash, Chalkboard, Users, Baby } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  createClassroom,
  deleteClassroom,
  getClassroom,
  listClassrooms,
  updateClassroom,
} from "@/lib/admin/classrooms";
import { centsToUsd } from "@/lib/billing";

const DELETE_REASON = {
  has_children:
    "This classroom still has enrolled children. Move them to another classroom first.",
  has_staff:
    "This classroom has active staff assignments. End them first from the Staff page.",
  blocked:
    "This classroom can't be deleted — it still has linked records.",
};

export default function AdminClassrooms() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listClassrooms();
      setRows(list);
    } catch (err) {
      toast.error(err.message || "Could not load classrooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.age_group || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    enrolled: rows.reduce((a, r) => a + r.enrolled_count, 0),
    capacity: rows.reduce((a, r) => a + (r.capacity || 0), 0),
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const res = await deleteClassroom(id);
      if (!res.ok) {
        toast.error(DELETE_REASON[res.reason] || "Could not delete");
        return;
      }
      toast.success(`Deleted ${name}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-classrooms" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Classrooms
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Configure classrooms, operational capacity, and monthly tuition. Licensed capacity and coverage rules live in Ratio & Capacity.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-classrooms-new"
            onClick={() => setDrawer({ mode: "create" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <Plus size={14} weight="bold" />
            New classroom
          </button>
          <Link
            to="/admin/ratio-capacity"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary-hover hover:bg-primary/10"
          >
            Ratio & Capacity
          </Link>
        </div>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <Kpi testId="admin-classrooms-kpi-total" label="Classrooms" value={String(kpis.total)} />
        <Kpi testId="admin-classrooms-kpi-enrolled" label="Enrolled children" value={String(kpis.enrolled)} accent />
        <Kpi testId="admin-classrooms-kpi-capacity" label="Total capacity" value={kpis.capacity ? String(kpis.capacity) : "—"} />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <input
            data-testid="admin-classrooms-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or age group"
            className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </section>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyCard testId="admin-classrooms-empty" title="No classrooms yet" desc="Create your first classroom to start assigning children and staff." />
      ) : (
        <ul data-testid="admin-classrooms-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filtered.map((r) => (
            <li
              key={r.id}
              data-testid={`admin-classrooms-card-${r.id}`}
              className="rounded-2xl border border-neutral-200 bg-white p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/30">
                      <Chalkboard size={18} weight="duotone" className="text-primary-hover" />
                    </span>
                    <div>
                      <p className="font-heading text-base font-semibold text-neutral-900">{r.name}</p>
                      <p className="text-xs text-neutral-500">
                        {r.age_group || "No age group"}
                        {r.location?.name ? ` · ${r.location.name}` : ""}
                        {r.capacity ? ` · Plan ${r.capacity}` : ""}
                        {r.licensed_capacity != null ? ` · Licensed ${r.licensed_capacity}` : " · Licensed cap not set"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid={`admin-classrooms-card-${r.id}-edit`}
                    onClick={() => setDrawer({ mode: "edit", classroomId: r.id })}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                  >
                    <Pencil size={11} weight="bold" />
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-classrooms-card-${r.id}-delete`}
                    onClick={() => onDelete(r.id, r.name)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                  >
                    <Trash size={11} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3 border-t border-neutral-100 pt-4 text-sm">
                <Stat icon={<Baby size={14} weight="duotone" />} label="Enrolled" value={r.enrolled_count} accent={r.capacity ? r.enrolled_count >= r.capacity : false} />
                <Stat icon={<Users size={14} weight="duotone" />} label="Staff" value={r.active_staff_count} />
                <Stat label="Tuition" value={r.monthly_tuition_cents != null ? centsToUsd(r.monthly_tuition_cents) : "—"} mono />
              </div>
            </li>
          ))}
        </ul>
      )}

      {drawer && (
        <ClassroomSheet
          mode={drawer.mode}
          classroomId={drawer.classroomId}
          organizationId={profile?.organization_id}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function ClassroomSheet({ mode, classroomId, organizationId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", age_group: "", capacity: "", monthly_tuition_dollars: "", required_staff_ratio: "", compliance_program_key: "" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (mode !== "edit" || !classroomId) return;
    (async () => {
      try {
        const c = await getClassroom(classroomId);
        if (!c) return;
        setDetail(c);
        setForm({
          name: c.name || "",
          age_group: c.age_group || "",
          capacity: c.capacity != null ? String(c.capacity) : "",
          monthly_tuition_dollars:
            c.monthly_tuition_cents != null ? (c.monthly_tuition_cents / 100).toFixed(2) : "",
          required_staff_ratio: c.required_staff_ratio != null ? String(c.required_staff_ratio) : "",
          compliance_program_key: c.compliance_program_key || "",
        });
      } catch (err) {
        toast.error(err.message || "Could not load classroom");
      }
    })();
  }, [mode, classroomId]);

  const onSave = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const patch = {
        name: form.name.trim(),
        age_group: form.age_group.trim() || null,
        capacity: form.capacity.trim() ? Math.max(0, parseInt(form.capacity, 10)) : null,
        monthly_tuition_cents: form.monthly_tuition_dollars.trim()
          ? Math.round(parseFloat(form.monthly_tuition_dollars) * 100)
          : null,
        required_staff_ratio: form.required_staff_ratio.trim()
          ? Math.max(1, Math.min(40, parseInt(form.required_staff_ratio, 10)))
          : null,
        compliance_program_key: form.compliance_program_key.trim().toLowerCase() || null,
      };
      if (mode === "create") {
        await createClassroom({
          organizationId,
          name: patch.name,
          ageGroup: patch.age_group,
          capacity: patch.capacity,
          monthlyTuitionCents: patch.monthly_tuition_cents,
          complianceProgramKey: patch.compliance_program_key,
        });
        toast.success("Classroom created");
      } else {
        await updateClassroom(classroomId, patch);
        toast.success("Classroom updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
  const activeChildren = (detail?.children || []).filter((c) => !c.withdrawn_at);

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-classroom-sheet">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "New classroom" : form.name || "Classroom"}</SheetTitle>
          <SheetDescription>Set the basics. Monthly tuition here is the fallback used by bulk tuition generation.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Name</span>
            <input data-testid="admin-classroom-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sunflower" className={input} />
          </label>

          <label className="block rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Compliance program key <span className="font-normal normal-case text-neutral-400">(optional)</span></span>
            <input
              data-testid="admin-classroom-compliance-program"
              value={form.compliance_program_key}
              onChange={(e) => setForm({ ...form, compliance_program_key: e.target.value })}
              placeholder="e.g. toddler"
              className={input}
            />
            <p className="mt-1.5 text-[11px] text-neutral-500">Use this only when a health requirement is intentionally scoped to a classroom program. It is not a regulatory classification.</p>
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Age group</span>
            <input data-testid="admin-classroom-age-group" value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} placeholder="Toddlers (18–36 mo)" className={input} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Capacity</span>
              <input data-testid="admin-classroom-capacity" type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="12" className={input} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Monthly tuition ($)</span>
              <input data-testid="admin-classroom-tuition" type="number" step="0.01" min="0" value={form.monthly_tuition_dollars} onChange={(e) => setForm({ ...form, monthly_tuition_dollars: e.target.value })} placeholder="1450.00" className={`${input} font-mono`} />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              Staff ratio <span className="font-normal normal-case text-neutral-400">(1 staff per N children — leave blank for no ratio)</span>
            </span>
            <div className="flex">
              <span className="flex items-center rounded-l-xl border border-r-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">1 :</span>
              <input
                data-testid="admin-classroom-ratio"
                type="number"
                min="1"
                max="40"
                value={form.required_staff_ratio}
                onChange={(e) => setForm({ ...form, required_staff_ratio: e.target.value })}
                placeholder="4"
                className={`${input} rounded-l-none font-mono`}
              />
            </div>
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="admin-classroom-save"
              disabled={saving}
              onClick={onSave}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              {saving ? "Saving…" : mode === "create" ? "Create classroom" : "Save changes"}
            </button>
          </div>

          {mode === "edit" && detail && (
            <div className="space-y-3 border-t border-neutral-100 pt-4">
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  Enrolled ({activeChildren.length})
                </p>
                <ul data-testid="admin-classroom-children" className="space-y-1 text-sm">
                  {activeChildren.length === 0 ? (
                    <li className="text-xs text-neutral-500">No enrolled children.</li>
                  ) : (
                    activeChildren.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 text-neutral-700">
                        <Baby size={12} weight="duotone" />
                        {c.first_name} {c.last_name}
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  Staff ({(detail.assignments || []).filter((a) => !a.ends_on || a.ends_on >= new Date().toISOString().slice(0, 10)).length})
                </p>
                <ul data-testid="admin-classroom-staff" className="space-y-1 text-sm">
                  {(detail.assignments || []).length === 0 ? (
                    <li className="text-xs text-neutral-500">No staff assigned.</li>
                  ) : (
                    detail.assignments.map((a) => (
                      <li key={a.id} className="flex items-center gap-2 text-neutral-700">
                        <Users size={12} weight="duotone" />
                        {a.profile?.full_name || a.profile?.email} · {a.position}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Stat({ icon, label, value, accent, mono }) {
  return (
    <div>
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-500">
        {icon}
        {label}
      </p>
      <p className={`mt-1 font-heading text-base font-semibold ${accent ? "text-[#C6851E]" : "text-neutral-900"} ${mono ? "font-mono text-sm" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" /></div>;
}

function EmptyCard({ title, desc, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <p className="font-heading text-base font-semibold text-neutral-900">{title}</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">{desc}</p>
    </div>
  );
}
